// src/lib/appPrefs.ts -- Apps page personalisation helpers (2026-11-04).
//
//   * sortApps      -- alphabetical by default; "recent" puts the apps the user
//                      has launched most recently first, then the rest A-Z.
//   * pin bay       -- PIN_SLOTS slots holding app ids; one of them may be the
//                      home-page app (its launcher shows on Home).
//   * recordLaunch  -- fire-and-forget POST so the server stamps "last used".
//
// All state lives in hub_users.preferences (no migration): appSort, pins,
// homeApp, appLastUsed. The server validates shape; this module keeps the
// SPA-side reads consistent and tolerant of a stale /api/me.

import { apiFetch } from "../api";
import { PIN_SLOTS, type AppSortKey, type UserPreferences } from "../auth";
import type { AppEntry } from "./apps";

export { PIN_SLOTS };

export const APP_SORT_LABELS: Readonly<Record<AppSortKey, string>> = {
  alpha: "A–Z",
  recent: "Recent",
};

const collator = new Intl.Collator("en-GB", { sensitivity: "base", numeric: true });

export function byName(a: AppEntry, b: AppEntry): number {
  return collator.compare(a.name, b.name);
}

export function sortApps(
  apps: ReadonlyArray<AppEntry>,
  sort: AppSortKey,
  lastUsed: Readonly<Record<string, string>> | undefined,
): AppEntry[] {
  const list = [...apps];
  if (sort !== "recent" || !lastUsed) return list.sort(byName);
  const stamp = (a: AppEntry): number => {
    const v = lastUsed[a.id];
    const t = v ? Date.parse(v) : NaN;
    return Number.isNaN(t) ? 0 : t;
  };
  return list.sort((a, b) => {
    const ta = stamp(a);
    const tb = stamp(b);
    if (ta !== tb) return tb - ta; // most recent first; never-used (0) last
    return byName(a, b);
  });
}

export function readPins(prefs: UserPreferences | null | undefined): string[] {
  const pins = Array.isArray(prefs?.pins) ? prefs.pins.filter((p): p is string => typeof p === "string") : [];
  return pins.slice(0, PIN_SLOTS);
}

export function readHomeApp(prefs: UserPreferences | null | undefined): string | null {
  const home = prefs?.homeApp ?? null;
  return home && readPins(prefs).includes(home) ? home : null;
}

export function readAppSort(prefs: UserPreferences | null | undefined): AppSortKey {
  return prefs?.appSort === "recent" ? "recent" : "alpha";
}

/** Best-effort: stamp the launch server-side. Never throws, never awaited by
 *  navigation. */
export function recordLaunch(appId: string): void {
  void apiFetch("/api/me/app-launch", {
    method: "POST",
    body: JSON.stringify({ appId }),
  }).catch(() => { /* last-used is a convenience; stay silent */ });
}

export async function savePins(pins: string[], homeApp: string | null): Promise<void> {
  await apiFetch("/api/me/preferences", {
    method: "PUT",
    body: JSON.stringify({ pins, homeApp }),
  });
}

export async function saveAppSort(appSort: AppSortKey): Promise<void> {
  await apiFetch("/api/me/preferences", {
    method: "PUT",
    body: JSON.stringify({ appSort }),
  });
}
